import type { PasswordInputStrength } from '@ksyos/design-system';
import { Box, Button, Card, FormGroup, Modal, PasswordInput, SuccessAnimation, Text } from '@ksyos/design-system';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';
import { MINIMUM_PASSWORD_LENGTH } from '../../../../common/practitionerDomain';
import { AbortableFetch } from '../../../store/fetch';
import { translateKey } from '../../../translate';

export interface IChangePasswordProps {
    practitionerId: string;
}

export function ChangePassword(props: IChangePasswordProps) {
    const abortableFetch = new AbortableFetch();
    const [showModal, setShowModal] = useState<boolean>(false);
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState<ValidationErrorCode | undefined>();
    const [passwordIsValid, setPasswordIsValid] = useState<boolean>(false);
    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const [passwordStrength, setPasswordStrength] = useState<PasswordInputStrength>(0);
    const [isValidatingPassword, setIsValidatingPassword] = useState<boolean>(false);

    const debouncedValidatePassword = useCallback(debounce(validatePassword, 200), []);

    useEffect(() => {
        return () => abortableFetch.abort();
    }, []);

    return (
        <>
            <Card>
                <Card.Header title={translateKey('common.password')} />
                <Card.Body>
                    <Text mb={4}>Kies een sterk wachtwoord dat je nergens anders gebruikt.</Text>
                    <Button
                        onClick={() => setShowModal(true)}
                        variant="link"
                    >
                        {translateKey('common.changePassword')}
                    </Button>
                </Card.Body>
            </Card>

            <Modal
                isOpen={showModal}
                onClose={() => onClose()}
                size="small"
            >
                {!showSuccess && (
                    <>
                        <Modal.Header>Wachtwoord wijzigen</Modal.Header>
                        <Modal.CloseButton></Modal.CloseButton>
                    </>
                )}
                <Modal.Body>
                    {!showSuccess && (
                        <FormGroup
                            label={`Vul een wachtwoord in van minimaal ${MINIMUM_PASSWORD_LENGTH} karakters`}
                            layout="vertical"
                        >
                            <PasswordInput
                                capsLockText="Capslock staat aan"
                                error={passwordError ? translateKey(`login.error.${passwordError}`) : ''}
                                onChange={async (e) => {
                                    setPassword(e.target.value);
                                    await debouncedValidatePassword(e.target.value);
                                }}
                                strength={passwordStrength}
                                value={password}
                            />
                        </FormGroup>
                    )}
                    {showSuccess && (
                        <>
                            <Box
                                alignItems="center"
                                display="flex"
                                flexDirection="column"
                            >
                                <SuccessAnimation size="large" />
                            </Box>
                            <Text textAlign="center">Gelukt! Je wachtwoord is gewijzigd.</Text>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {!showSuccess && passwordIsValid && (
                        <Button
                            className="fade-in"
                            onClick={async () => {
                                if (!isValidatingPassword) {
                                    await changePassword();
                                }
                            }}
                            width="full"
                        >
                            {translateKey('request.change')}
                        </Button>
                    )}
                    {showSuccess && (
                        <Button
                            onClick={() => onClose()}
                            width="full"
                        >
                            {translateKey('common.close')}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );

    function onClose() {
        setShowModal(false);
        setShowSuccess(false);
        setPasswordStrength(0);
        setPassword('');
    }

    async function validatePassword(newPassword: string) {
        setIsValidatingPassword(true);
        let isPasswordValid = false;
        if (!newPassword) {
            setPasswordError(ValidationErrorCode.NO_PASSWORD);
            setPasswordStrength(0);
        } else if (newPassword.length < MINIMUM_PASSWORD_LENGTH / 2) {
            setPasswordError(ValidationErrorCode.VERY_SHORT_PASSWORD);
            setPasswordStrength(1);
        } else if (newPassword.length < MINIMUM_PASSWORD_LENGTH) {
            setPasswordError(ValidationErrorCode.SHORT_PASSWORD);
            setPasswordStrength(2);
        } else {
            const result = await abortableFetch.fetchJson(
                '/api/validatePassword/',
                'POST',
                { practitionerId: props.practitionerId, password: newPassword },
                [
                    ValidationErrorCode.BAD_QUALITY_PASSWORD,
                    ValidationErrorCode.PASSWORD_CONTAINS_FREQUENTLY_USED_PASSWORD,
                    ValidationErrorCode.PASSWORD_CONTAINS_PERSONAL_INFORMATION,
                    ValidationErrorCode.PASSWORD_CONTAINS_REPEATED_SUBSEQUENCE,
                ]
            );

            if (result.errorCode) {
                setPasswordError(result.errorCode as ValidationErrorCode);
                setPasswordStrength(3);
            } else if (result.status === 200) {
                setPasswordError(undefined);
                isPasswordValid = true;
                setPasswordStrength(result.json.strength + 1);
            }
        }

        setPasswordIsValid(isPasswordValid);
        setIsValidatingPassword(false);
    }

    async function changePassword() {
        await abortableFetch.fetchJson(`/api/practitioners/${props.practitionerId}/changePassword`, 'POST', {
            practitionerId: props.practitionerId,
            password,
        });
        setShowSuccess(true);
        setPassword('');
    }
}
